import { ArrowDownThin } from '@ori-ui/icons';
import { Typography, Collapse } from '@ori-ui/mui';
import type { GlobalCustomerRegistrationsRegistrationLegalDetailsDto } from '@ori/global-api-client-react';
import type { StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto } from '@ori/static-api-client-react';
import type { FC } from 'react';
import { useState } from 'react';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { TranslationKey } from '../../../models';
import { CheckboxFormFieldByConfig } from '../../FieldsByConfig';
import { CollapseBox } from './StyledComponentsLegalForm';

interface ILegalDetailApprovalsProps {
  config?: StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto;
  approvalValues: GlobalCustomerRegistrationsRegistrationLegalDetailsDto;
}

export const LegalDetailApprovals: FC<ILegalDetailApprovalsProps> = (props) => {
  const { approvalValues, config } = props;
  const [showApprovals, setShowApprovals] = useState(false);

  const isNegativeApprovals = config?.approvals?.shared?.displayAsNegative;

  return (
    <>
      {isNegativeApprovals ? (
        <CollapseBox onClick={() => setShowApprovals(!showApprovals)}>
          {showApprovals ? (
            <Localization
              id={TranslationKey.ThirdStep_ShowLess}
              defaultMessage={defaultTranslations.ThirdStep_ShowLess}
            />
          ) : (
            <Localization
              id={TranslationKey.ThirdStep_ShowMore}
              defaultMessage={defaultTranslations.ThirdStep_ShowMore}
            />
          )}

          <ArrowDownThin
            sx={{
              transform: showApprovals ? 'rotateX(180deg)' : '0',
              transition: 'all 0.2s ease-in-out',
            }}
          />
        </CollapseBox>
      ) : null}

      <Collapse in={isNegativeApprovals ? showApprovals : true}>
        <CheckboxFormFieldByConfig
          name="isReseller"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.isReseller ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Reseller}
                defaultMessage={defaultTranslations.Reseller}
              />
            </Typography>
          }
          value={approvalValues.isReseller}
          {...config?.isReseller}
        />
        <CheckboxFormFieldByConfig
          name="approvals.contact"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.contact ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_Contact}
                defaultMessage={defaultTranslations.Approval_Contact}
              />
            </Typography>
          }
          value={approvalValues.approvals?.contact}
          {...config?.approvals?.contact}
        />
        <CheckboxFormFieldByConfig
          name="approvals.blockGuaranteeOrdering"
          label={
            <Typography
              variant="body1"
              sx={{
                fontWeight: approvalValues.approvals?.blockGuaranteeOrdering ? 'bold' : 'normal',
              }}
            >
              <Localization
                id={TranslationKey.Approval_BlockGuaranteeOrdering}
                defaultMessage={defaultTranslations.Approval_BlockGuaranteeOrdering}
              />
            </Typography>
          }
          value={approvalValues.approvals?.blockGuaranteeOrdering}
          {...config?.approvals?.blockGuaranteeOrdering}
        />
        <CheckboxFormFieldByConfig
          name="approvals.marketingConsultant"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.marketingConsultant ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_MarketingConsultant}
                defaultMessage={defaultTranslations.Approval_MarketingConsultant}
              />
            </Typography>
          }
          value={approvalValues.approvals?.marketingConsultant}
          {...config?.approvals?.marketingConsultant}
        />
        <CheckboxFormFieldByConfig
          name="approvals.marketingExternal"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.marketingExternal ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_MarketingExternal}
                defaultMessage={defaultTranslations.Approval_MarketingExternal}
              />
            </Typography>
          }
          value={approvalValues.approvals?.marketingExternal}
          {...config?.approvals?.marketingExternal}
        />
        <CheckboxFormFieldByConfig
          name="approvals.marketingOriflame"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.marketingOriflame ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_MarketingOriflame}
                defaultMessage={defaultTranslations.Approval_MarketingOriflame}
              />
            </Typography>
          }
          value={approvalValues.approvals?.marketingOriflame}
          {...config?.approvals?.marketingOriflame}
        />
        <CheckboxFormFieldByConfig
          name="approvals.newsletters"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.newsletters ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_Newletters}
                defaultMessage={defaultTranslations.Approval_Newletters}
              />
            </Typography>
          }
          value={approvalValues.approvals?.newsletters}
          {...config?.approvals?.newsletters}
        />
        <CheckboxFormFieldByConfig
          name="approvals.optedOutOfSponsorSelect"
          label={
            <Typography
              variant="body1"
              sx={{
                fontWeight: approvalValues.approvals?.optedOutOfSponsorSelect ? 'bold' : 'normal',
              }}
            >
              <Localization
                id={TranslationKey.optedOutOfSponsorSelect}
                defaultMessage={defaultTranslations.optedOutOfSponsorSelect}
              />
            </Typography>
          }
          value={approvalValues.approvals?.optedOutOfSponsorSelect}
          {...config?.approvals?.optedOutOfSponsorSelect}
        />
        <CheckboxFormFieldByConfig
          name="approvals.orderHistory"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.orderHistory ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.orderHistory}
                defaultMessage={defaultTranslations.orderHistory}
              />
            </Typography>
          }
          value={approvalValues.approvals?.orderHistory}
          {...config?.approvals?.orderHistory}
        />
        <CheckboxFormFieldByConfig
          name="approvals.personalData"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.personalData ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_PersonalData}
                defaultMessage={defaultTranslations.Approval_PersonalData}
              />
            </Typography>
          }
          value={approvalValues.approvals?.personalData}
          {...config?.approvals?.personalData}
        />
        <CheckboxFormFieldByConfig
          name="approvals.publication"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.publication ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_Publication}
                defaultMessage={defaultTranslations.Approval_Publication}
              />
            </Typography>
          }
          value={approvalValues.approvals?.publication}
          {...config?.approvals?.publication}
        />
        <CheckboxFormFieldByConfig
          name="approvals.uplineLeaderBrowse"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.uplineLeaderBrowse ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_UplineLeaderBrowse}
                defaultMessage={defaultTranslations.Approval_UplineLeaderBrowse}
              />
            </Typography>
          }
          value={approvalValues.approvals?.uplineLeaderBrowse}
          {...config?.approvals?.uplineLeaderBrowse}
        />
        <CheckboxFormFieldByConfig
          name="approvals.uplineLeaderData"
          label={
            <Typography
              variant="body1"
              sx={{ fontWeight: approvalValues.approvals?.uplineLeaderData ? 'bold' : 'normal' }}
            >
              <Localization
                id={TranslationKey.Approval_UplineLeaderData}
                defaultMessage={defaultTranslations.Approval_UplineLeaderData}
              />
            </Typography>
          }
          value={approvalValues.approvals?.uplineLeaderData}
          {...config?.approvals?.uplineLeaderData}
        />
      </Collapse>
    </>
  );
};
